import { classNames } from "primereact/utils";
import { ReviewQuote } from "../../../../reviews/components/ReviewQuote/ReviewQuote";
import styles from "./StartReservationStep.module.scss";
import { useAppContext } from "../../../../../AppContext";
import { useStudioWorkingHours } from "../../hooks";
import {
  generateMapsLink,
  routePaths,
} from "../../../../../common/infrastructure/routes/routePaths";
import { useNavigate } from "react-router";
import { RenderWhen } from "../../../../../common/infrastructure/components/RenderWhen";
import { useMemo, useEffect, useState } from "react";
import { workingHoursUtility } from "../../../../../common/infrastructure/utilities/workingHoursUtility";
import { KeyIcon } from "../../../../../common/icons/baseIcons/KeyIcon";
import { EmojiIcon } from "../../../../../common/icons/emoji/EmojiIcon";
import { AppDialog } from "../../../../../common/components/AppDialog/AppDialog";
import { SelectStudioForm } from "../../../../../common/components/layout/components/SelectStudioForm";
import { FaqDialog } from "../../../faq";
import { useImageWithFallback } from "../../../../../common/infrastructure/hooks/useImageWithFallback";

interface IStartReservationStepProps {
  className?: string;
  onContinue: () => void;
}

export const StartReservationStep = ({
  className,
  onContinue,
}: IStartReservationStepProps) => {
  const {
    selectedStudio,
    showGroupTypeOptions,
    showServicesTypeOptions,
    isAuthenticated,
    userStudios,
    isDesktopOrTablet,
  } = useAppContext();
  const { studioHours, hasData } = useStudioWorkingHours(selectedStudio?.id);
  const navigate = useNavigate();
  const [showStudioDialog, setShowStudioDialog] = useState(false);
  const [showFaqDialog, setShowFaqDialog] = useState(false);
  const studioLogo = useImageWithFallback(selectedStudio?.logoUrl);

  // Auto-redirect to group class wizard if there's saved state
  useEffect(() => {
    const groupClassBackup = localStorage.getItem(
      "groupClassWizardStateBackup",
    );

    if (groupClassBackup) {
      try {
        const parsedBackup = JSON.parse(groupClassBackup);

        // Check if backup hasn't expired (10 minutes)
        const now = Date.now();
        const backupAge = now - (parsedBackup.timestamp || 0);
        const isExpired = backupAge > 10 * 60 * 1000;

        if (!isExpired && parsedBackup.selection) {
          // Valid backup exists - redirect to group class wizard
          navigate(routePaths.portalGroupClassWizard);
        } else if (isExpired) {
          // Clean up expired backup
          localStorage.removeItem("groupClassWizardStateBackup");
        }
      } catch (e) {
        console.error("Failed to parse group class backup:", e);
        localStorage.removeItem("groupClassWizardStateBackup");
      }
    }
  }, [navigate]);

  const workingHours = useMemo(
    () =>
      hasData ? workingHoursUtility.formatWorkingHoursText(studioHours) : null,
    [hasData, studioHours],
  );

  const canChangeStudio = userStudios.length > 1;

  return (
    <div className={classNames(styles.container, className)}>
      {/* Welcome Section */}
      <div className={styles.welcomeSection}>
        {/* The studio's own logo when it has one; the sparkle is only a stand-in for studios
            that have not uploaded one yet — and for a logo link that no longer loads. */}
        {studioLogo.showImage ? (
          <img
            className={styles.welcomeLogo}
            src={selectedStudio!.logoUrl}
            alt={selectedStudio!.name}
            onError={studioLogo.onError}
          />
        ) : (
          <div className={styles.welcomeIcon}>
            <EmojiIcon name="sparkles" />
          </div>
        )}
        <h2 className={styles.welcomeTitle}>Добре дошли!</h2>
        <ReviewQuote />
        {/* <p className={styles.welcomeSubtitle}>
          Готови сме да ви предложим най-добрите услуги за грижа
        </p> */}
      </div>

      {/* Action Cards */}
      <div className={styles.actionCardsContainer}>
        <RenderWhen condition={showServicesTypeOptions}>
          <div className={styles.actionCard} onClick={onContinue}>
            <div className={styles.actionIcon}>
              <EmojiIcon name="calendar" />
            </div>
            <div className={styles.actionContent}>
              <h3 className={styles.actionTitle}>Запази час</h3>
              <p className={styles.actionDescription}>
                Изберете услуга и резервирайте час при нас
              </p>
            </div>
            <div className={styles.actionArrow}>→</div>
          </div>
        </RenderWhen>

        <RenderWhen
          condition={
            showGroupTypeOptions && !selectedStudio?.useGroupClassCategories
          }
        >
          <div
            className={styles.actionCard}
            onClick={() => navigate(routePaths.calendar)}
          >
            <div className={styles.actionIcon}>
              <EmojiIcon name="womanRunning" />
            </div>
            <div className={styles.actionContent}>
              <h3 className={styles.actionTitle}>Календар групови занимания</h3>
              <p className={styles.actionDescription}>
                Резервирайте своето място тук
              </p>
            </div>
            <div className={styles.actionArrow}>→</div>
          </div>
        </RenderWhen>

        <RenderWhen
          condition={
            showGroupTypeOptions && !!selectedStudio?.useGroupClassCategories
          }
        >
          <div
            className={styles.actionCard}
            onClick={() => navigate(routePaths.portalGroupClassWizard)}
          >
            <div className={styles.actionIcon}>
              <EmojiIcon name="womanRunning" />
            </div>
            <div className={styles.actionContent}>
              <h3 className={styles.actionTitle}>Видове класове</h3>
              <p className={styles.actionDescription}>
                Резервирайте своето място тук
              </p>
            </div>
            <div className={styles.actionArrow}>→</div>
          </div>
        </RenderWhen>

        <RenderWhen
          condition={
            showGroupTypeOptions &&
            !!selectedStudio?.useGroupClassCategories &&
            !!selectedStudio?.showCalendarTileInPortal
          }
        >
          <div
            className={styles.actionCard}
            onClick={() => navigate(routePaths.calendar)}
          >
            <div className={styles.actionIcon}>
              <EmojiIcon name="tearOffCalendar" />
            </div>
            <div className={styles.actionContent}>
              <h3 className={styles.actionTitle}>Календар</h3>
              <p className={styles.actionDescription}>
                Разгледайте груповите занимания директно в нашия календар
              </p>
            </div>
            <div className={styles.actionArrow}>→</div>
          </div>
        </RenderWhen>

        <RenderWhen condition={!isAuthenticated}>
          <div
            className={styles.actionCard}
            onClick={() => navigate(routePaths.login)}
          >
            <div className={styles.actionIcon}>
              <KeyIcon color="rgba(255, 200, 80, 0.95)" />
            </div>
            <div className={styles.actionContent}>
              <h3 className={styles.actionTitle}>Вход или Регистрация</h3>
              <p className={styles.actionDescription}>
                Влезте в профила си или се регистрирайте
              </p>
            </div>
            <div className={styles.actionArrow}>→</div>
          </div>
        </RenderWhen>
      </div>

      {/* Additional Info */}
      <div className={styles.infoSection}>
        <RenderWhen condition={canChangeStudio}>
          <div
            className={classNames(styles.studioCard, {
              [styles.clickable]: canChangeStudio,
            })}
            onClick={
              canChangeStudio ? () => setShowStudioDialog(true) : undefined
            }
          >
            <div className={styles.studioCardLeft}>
              <div className={styles.studioCardIcon}>
                <EmojiIcon name="departmentStore" />
              </div>
              <div className={styles.studioCardInfo}>
                <div className={styles.studioCardLabel}>Избран обект</div>
                <div className={styles.studioCardName}>
                  {selectedStudio?.name}
                </div>
              </div>
            </div>
            <div className={styles.studioCardChange}>Промени →</div>
          </div>
        </RenderWhen>

        <RenderWhen condition={!!workingHours}>
          <div className={styles.infoCard}>
            <div className={styles.infoIcon}>
              <EmojiIcon name="oneOClock" />
            </div>
            <div className={styles.infoText}>
              <strong>Работно време:</strong>
              <br />
              <div style={{ whiteSpace: "pre-line" }}>{workingHours}</div>
            </div>
          </div>
        </RenderWhen>

        <div
          className={classNames(styles.infoCard, styles.clickable)}
          onClick={() =>
            window.open(
              generateMapsLink(
                selectedStudio?.address,
                selectedStudio?.addressMapUrl,
              ),
              "_blank",
              "noopener,noreferrer",
            )
          }
        >
          <div className={styles.infoIcon}>
            <EmojiIcon name="roundPushpin" />
          </div>
          <div className={styles.infoText}>
            <strong>Адрес:</strong>
            <br />
            {selectedStudio?.address}
          </div>
        </div>

        <div
          className={classNames(styles.infoCard, styles.clickable)}
          onClick={() => setShowFaqDialog(true)}
        >
          <div className={styles.infoIcon}>
            <EmojiIcon name="redQuestionMark" />
          </div>
          <div className={styles.infoText}>
            <strong>Често задавани въпроси</strong>
            <br />
            Как да откажа резервация, как да се регистрирам и още
          </div>
        </div>
      </div>

      <FaqDialog
        visible={showFaqDialog}
        onHide={() => setShowFaqDialog(false)}
      />

      <AppDialog
        header="Избиране на обект"
        visible={showStudioDialog}
        style={{ width: isDesktopOrTablet ? "450px" : "90vw" }}
        onHide={() => setShowStudioDialog(false)}
      >
        <SelectStudioForm setShowSelectStudioDialog={setShowStudioDialog} />
      </AppDialog>
    </div>
  );
};
